
import html2canvas from 'html2canvas';
import html2pdf from 'html2pdf.js';
import { jsPDF } from 'jspdf';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import "react-datepicker/dist/react-datepicker.css";
import { useFieldArray, useForm } from "react-hook-form";
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import filterSolidIcon from '../../assets/images/filter-solid-icon.svg';
import { PersonalIncomeFormI } from '../../resources/form-props';
import { lastYears } from '../../resources/functions';
import { useFourThreeThreeBApi } from '../../resources/hooks/api/fourThreeThreeBApiHook';
import "./style.css";

import ScrollToggle from '../../components/ScrollToggle/ScrollToggle';
import { getBranch } from '../../store/user/selectors';
interface Props {

}

type SortDirection = 'asc' | 'desc'
type SortField = 'monthly_amount' | 'annual_amount'
type MainSectionKey = 'section1' | 'section2' | 'section3' | 'section4'

interface SectionSortConfig {
    field: SortField | null
    direction: SortDirection | null
}

const getDefaultSortConfig = (): Record<MainSectionKey, SectionSortConfig> => ({
    section1: { field: null, direction: null },
    section2: { field: null, direction: null },
    section3: { field: null, direction: null },
    section4: { field: null, direction: null },
});

const HeaderIcon = ({
    children,
    className = '',
}: {
    children: ReactNode
    className?: string
}) => (
    <svg
        className={className}
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="currentColor"
        aria-hidden="true"
    >
        {children}
    </svg>
)

const SortIndicatorIcon = ({
    direction,
}: {
    direction: SortDirection | null
}) => {
    if (direction === 'asc') {
        return (
            <HeaderIcon className="section-sort-icon">
                <path
                    fillRule="evenodd"
                    d="M8 12a.5.5 0 0 0 .5-.5V3.707l2.146 2.147a.5.5 0 0 0 .708-.708l-3-3a.5.5 0 0 0-.708 0l-3 3a.5.5 0 1 0 .708.708L7.5 3.707V11.5A.5.5 0 0 0 8 12z"
                />
            </HeaderIcon>
        )
    }

    if (direction === 'desc') {
        return (
            <HeaderIcon className="section-sort-icon">
                <path
                    fillRule="evenodd"
                    d="M8 4a.5.5 0 0 0-.5.5v7.793L5.354 10.146a.5.5 0 1 0-.708.708l3 3a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 12.293V4.5A.5.5 0 0 0 8 4z"
                />
            </HeaderIcon>
        )
    }

    return (
        <HeaderIcon className="section-sort-icon section-sort-icon--muted">
            <path
                fillRule="evenodd"
                d="M11.5 15a.5.5 0 0 0 .5-.5V2.707l1.146 1.147a.5.5 0 0 0 .708-.708l-2-2a.5.5 0 0 0-.708 0l-2 2a.5.5 0 1 0 .708.708L11 2.707V14.5a.5.5 0 0 0 .5.5zm-7-14a.5.5 0 0 0-.5.5v11.793L2.854 12.146a.5.5 0 1 0-.708.708l2 2a.5.5 0 0 0 .708 0l2-2a.5.5 0 1 0-.708-.708L5 13.293V1.5a.5.5 0 0 0-.5-.5z"
            />
        </HeaderIcon>
    )
}

const PersonalIncomeStatement: React.FC<Props> = () => {
    const { t } = useTranslation('scenes.PersonalIncomeStatement');
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const initData = {
        section_one_primary_income: [],
        section_one_other_income: [],
        section_two_fixed_expense: [],
        section_two_variable_expense: [],
        section_three_investments: [],
        section_four_budgets: [],
    };
    const branchData = useSelector(getBranch)
    const pagesRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tableContainerRef = useRef<HTMLDivElement>(null);

    const { getPersonalIncomeStatment, createPersonalIncomeStatment } = useFourThreeThreeBApi();
    const [currentMonthIndex, setCurrentMonthIndex] = useState<any>(new Date().getMonth());
    const currentMonthName = months[currentMonthIndex];
    const [showLoader, setShowLoader] = useState<boolean>(false)
    const [searchParams, setSearchParams] = useState<any>({
        month: { value: new Date().getMonth() + 1, label: currentMonthName },
        year: { value: new Date().getFullYear(), label: '' + new Date().getFullYear() }
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState<boolean>(false);

    const [totalSectionOneMonthlyIncome, setTotalSectionOneMonthlyIncome] = useState<number>(0);
    const [totalSectionOneAnnualIncome, setTotalSectionOneAnnualIncome] = useState<number>(0);

    const [totalSectionTwoFixedMonthlyIncome, setSectionTwoFixedMonthlyIncome] = useState<number>(0);
    const [totalSectionTwoFixedAnnualIncome, setSectionTwoFixedAnnualIncome] = useState<number>(0);

    const [totalSectionTwoVariableMonthlyIncome, setSectionTwoVariableMonthlyIncome] = useState<number>(0);
    const [totalSectionTwoVariableAnnualIncome, setSectionTwoVariableAnnualIncome] = useState<number>(0);

    const [totalSectionThreeMonthlyIncome, setSectionThreeMonthlyIncome] = useState<number>(0);
    const [totalSectionThreeAnnualIncome, setSectionThreeAnnualIncome] = useState<number>(0);

    const [totalSectionFourMonthlyIncome, setSectionFourMonthlyIncome] = useState<number>(0);
    const [totalSectionFourAnnualIncome, setSectionFourAnnualIncome] = useState<number>(0);
    const [sectionSortConfig, setSectionSortConfig] = useState<Record<MainSectionKey, SectionSortConfig>>(
        getDefaultSortConfig()
    );

    const { control, register, reset, setValue, getValues, watch, handleSubmit, formState: { errors } } = useForm<PersonalIncomeFormI>({
        defaultValues: initData,
        // resolver: yupResolver(PersonalIncomeValidate)
    });

    useEffect(() => {
        // reset(initData);
        // initialiseForm();
        fetchMonthlyIncome();
    }, [])

    const initialiseForm = () => {

        addMoreSectionOneOtherIncome([
            { title: t('childSupportAlimony'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('socialSecurityIncome'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('disabilityIncome'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('pensions'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('investments'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('realEstateInvestments'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('businessIncome'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('otherIncome'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: false, monthly_amount_read_only: false, annual_amount_read_only: true },
        ]);
        addMoreSectionTwoFixedExpense([
            { title: t('payrollTaxes'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('otherIncomeDeductions'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('rentMortgage'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('loansHomeEquity'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('propertyTaxes'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('water'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('garbage'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('gasAndElectricBills'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('autoInsurance'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('autoRepairs'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('groceries'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('necessaryClothing'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('landlinePhone'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('homeRentersInsurance'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('healthcareInsurancePremium'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('dentalInsurancePremium'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('lifeInsurancePremium'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('studentLoanPaymentMonthly'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('childDependentSupportExpenses'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('alimonySpousalSupport'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('homeOwnersAssociationDues'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('otherFixed'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: false, monthly_amount_read_only: false, annual_amount_read_only: true },
        ]);
        addMoreSectionTwoVariableExpense([
            { title: t('creditCardPayments'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('autoLoanPayments'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('gas'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('cableOrTV'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('mobilePhone'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('homeImprovementRepairs'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('homeSupplies'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('dryCleaning'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('laundryOther'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('investmentRealEstateExpenses'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('childcare'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('childBabyExpensesOther'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('entertainment'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('diningOut'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('vacations'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('petCosts'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('gifts'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('internet'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('technology'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('otherVariable'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: false, monthly_amount_read_only: false, annual_amount_read_only: true },
        ]);
        addMoreSectionThreeInvestments([
            { title: t('iraDeposits'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: true, annual_amount_read_only: false },
            { title: t('employeeStockPlans'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('brokerageDeposits'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('otherInvestments'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: false, monthly_amount_read_only: false, annual_amount_read_only: true },
        ]);
        addMoreSectionFourBudgets([
            { title: t('totalIncome'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: true, annual_amount_read_only: true },
            { title: t('fixedExpenses'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: true, annual_amount_read_only: true },
            { title: t('netIncome'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: true, annual_amount_read_only: true },
            { title: t('investments'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: true, annual_amount_read_only: true },
            { title: t('netIncomeMinusInvestments'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: true, annual_amount_read_only: true },
            { title: t('variableExpenses'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: true, annual_amount_read_only: true },
        ]);
        addMoreSectionOnePrimaryIncome([
            { title: t('yourIncome'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
            { title: t('yourSpousesIncome'), monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true },
        ]);

        setTotalSectionOneMonthlyIncome(0);
        setTotalSectionOneAnnualIncome(0);

        setSectionTwoFixedMonthlyIncome(0);
        setSectionTwoFixedAnnualIncome(0);

        setSectionTwoVariableMonthlyIncome(0);
        setSectionTwoVariableAnnualIncome(0);

        setSectionThreeMonthlyIncome(0);
        setSectionThreeAnnualIncome(0);

        setSectionFourMonthlyIncome(0);
        setSectionFourAnnualIncome(0);
        setSectionSortConfig(getDefaultSortConfig());

        setTimeout(() => {
            window.scrollTo(0, 0)
        }, 0);
    }

    const formatToUSD = (amount: number): string => {
        return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
    };
    const normalizeAmountValue = (value: number | string | null | undefined): number => {
        if ((value === '') || (value === null) || (typeof value === 'undefined')) {
            return 0;
        }

        const parsedValue = Number(value);

        if (Number.isNaN(parsedValue)) {
            return 0;
        }

        return Number(parsedValue.toFixed(2));
    };
    const formatAmountFieldValue = (value: number): string => {
        if (Number.isNaN(value)) {
            return '';
        }

        return normalizeAmountValue(value).toFixed(2);
    };
    const formatAmountDisplayValue = (value: number | string | null | undefined): string => {
        const normalizedValue = normalizeAmountValue(value);

        if (normalizedValue === 0) {
            return '0';
        }

        return formatAmountFieldValue(normalizedValue);
    };
    const isDefaultZeroDisplayValue = (value: string) => /^-?0(?:\.0+)?$/.test(value.trim());
    const getNumericInputRegisterOptions = (events: Record<string, any> = {}) => ({
        setValueAs: (value: string | number) => normalizeAmountValue(value),
        ...events,
    });
    const runAfterFieldUpdate = (callback: () => void) => {
        window.requestAnimationFrame(callback);
    };
    const handleMonthlyAmountChange = (rawValue: string, annualFieldName: any, calculateSection: () => void) => {
        const sanitizedValue = sanitizeAmountInputValue(rawValue);

        if ((sanitizedValue === '') || (sanitizedValue === '-')) {
            setValue(annualFieldName, formatAmountDisplayValue(0) as any);
            runAfterFieldUpdate(calculateSection);
            return;
        }

        const monthlyAmount = Number(sanitizedValue);

        if (Number.isNaN(monthlyAmount)) {
            return;
        }

        setValue(annualFieldName, formatAmountDisplayValue(monthlyAmount * 12) as any);
        runAfterFieldUpdate(calculateSection);
    };
    const handleMonthlyAmountBlur = (fieldName: any, annualFieldName: any, rawValue: string, calculateSection: () => void) => {
        const sanitizedValue = sanitizeAmountInputValue(rawValue);

        if ((sanitizedValue === '') || (sanitizedValue === '-')) {
            setValue(fieldName, formatAmountDisplayValue(0) as any);
            setValue(annualFieldName, formatAmountDisplayValue(0) as any);
            runAfterFieldUpdate(calculateSection);
            return;
        }

        const monthlyAmount = Number(sanitizedValue);

        if (Number.isNaN(monthlyAmount)) {
            return;
        }

        setValue(fieldName, formatAmountDisplayValue(monthlyAmount) as any);
        setValue(annualFieldName, formatAmountDisplayValue(monthlyAmount * 12) as any);
        runAfterFieldUpdate(calculateSection);
    };
    const handleMonthlyAmountFocus = (rawValue: string, input: HTMLInputElement) => {
        if (rawValue === '') {
            return;
        }

        if (normalizeAmountValue(rawValue) === 0) {
            window.setTimeout(() => {
                input.select();
                if (typeof input.setSelectionRange === 'function') {
                    input.setSelectionRange(0, input.value.length);
                }
            }, 0);
        }
    };
    const sanitizeAmountInputValue = (rawValue: string) => {
        if (rawValue === '') {
            return '';
        }

        const isNegative = rawValue.startsWith('-');
        const unsignedValue = isNegative ? rawValue.slice(1) : rawValue;
        const cleanedValue = unsignedValue.replace(/[^0-9.]/g, '');
        const hasDot = cleanedValue.includes('.');
        const [integerRaw = '', ...decimalParts] = cleanedValue.split('.');
        const decimalPart = decimalParts.join('').slice(0, 2);
        let integerPart = integerRaw.replace(/^0+(?=\d)/, '');

        if ((integerPart === '') && ((integerRaw !== '') || hasDot)) {
            integerPart = '0';
        }

        if ((integerPart === '') && (decimalPart === '')) {
            return isNegative ? '-' : '';
        }

        const prefix = isNegative ? '-' : '';

        if (hasDot && cleanedValue.endsWith('.') && (decimalPart === '')) {
            return `${prefix}${integerPart}.`;
        }

        if (decimalPart !== '') {
            return `${prefix}${integerPart}.${decimalPart}`;
        }

        return `${prefix}${integerPart}`;
    };
    const restrictToTwoDecimals = (e: React.FormEvent<HTMLInputElement>) => {
        const sanitizedValue = sanitizeAmountInputValue(e.currentTarget.value);

        if (e.currentTarget.value !== sanitizedValue) {
            e.currentTarget.value = sanitizedValue;
        }
    };
    const applyMonthlyAmountStep = (input: HTMLInputElement, direction: 'up' | 'down') => {
        if (input.readOnly) {
            return;
        }

        const currentValue = Number(input.value || 0);
        const nextValue = direction === 'up'
            ? Math.floor(currentValue) + 1
            : Math.floor(currentValue) - 1;

        input.value = formatAmountDisplayValue(nextValue);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.focus();
    };
    const handleMonthlyAmountStep = (event: React.MouseEvent<HTMLButtonElement>, direction: 'up' | 'down') => {
        event.preventDefault();

        const wrapper = event.currentTarget.closest('.number-input-with-stepper');
        const input = wrapper?.querySelector('input.monthly-amount-input') as HTMLInputElement | null;

        if (!input || input.readOnly) {
            return;
        }

        applyMonthlyAmountStep(input, direction);
    };
    const normalizeLoadedAmount = (value: any) => {
        return formatAmountDisplayValue(value);
    };
    const normalizeLoadedAmountRow = (item: any) => ({
        ...item,
        monthly_amount: normalizeLoadedAmount(item.monthly_amount),
        annual_amount: normalizeLoadedAmount(item.annual_amount),
    });
    const clearLoadedZeroAmount = (fieldName: any, value: any) => {
        setValue(fieldName, formatAmountDisplayValue(value) as any);
    };
    const calculateSectionOneTotalIncome = () => {
        let monthlyTotal = 0, annualTotal = 0;

        getValues().section_one_primary_income.forEach((field: any) => {
            monthlyTotal += +field.monthly_amount || 0;
        });
        getValues().section_one_other_income.forEach((field: any) => {
            monthlyTotal += +field.monthly_amount || 0;
        });

        getValues().section_one_primary_income.forEach((field: any) => {
            annualTotal += +field.annual_amount || 0;
        })
        getValues().section_one_other_income.forEach((field: any) => {
            annualTotal += +field.annual_amount || 0;
        })

        setTotalSectionOneMonthlyIncome(monthlyTotal);
        const fixedExpenseMonthly = Number(getValues('section_four_budgets.1.monthly_amount')) || 0;
        const fixedExpenseAnnual = Number(getValues('section_four_budgets.1.annual_amount')) || 0;
        const investmentMonthly = Number(getValues('section_four_budgets.3.monthly_amount')) || 0;
        const investmentAnnual = Number(getValues('section_four_budgets.3.annual_amount')) || 0;

        setValue('section_four_budgets.0.monthly_amount', formatAmountDisplayValue(monthlyTotal) as any);
        setValue('section_four_budgets.2.monthly_amount', formatAmountDisplayValue(monthlyTotal - fixedExpenseMonthly) as any);

        setTotalSectionOneAnnualIncome(annualTotal);
        setValue('section_four_budgets.0.annual_amount', formatAmountDisplayValue(annualTotal) as any);
        setValue('section_four_budgets.2.annual_amount', formatAmountDisplayValue(annualTotal - fixedExpenseAnnual) as any);

        setValue('section_four_budgets.4.monthly_amount', formatAmountDisplayValue(monthlyTotal - fixedExpenseMonthly - investmentMonthly) as any);
        setValue('section_four_budgets.4.annual_amount', formatAmountDisplayValue(annualTotal - fixedExpenseAnnual - investmentAnnual) as any);

        calculateSectionFourIncome();
    }
    const calculateSectionTwoFixedIncome = () => {
        let monthlyTotal = 0, annualTotal = 0;

        getValues().section_two_fixed_expense.forEach((field: any) => {
            monthlyTotal += +field.monthly_amount || 0;
        });
        getValues().section_two_fixed_expense.forEach((field: any) => {
            annualTotal += +field.annual_amount || 0;
        })

        setSectionTwoFixedMonthlyIncome(monthlyTotal);
        const totalIncomeMonthly = Number(getValues('section_four_budgets.0.monthly_amount')) || 0;
        const totalIncomeAnnual = Number(getValues('section_four_budgets.0.annual_amount')) || 0;
        const investmentMonthly = Number(getValues('section_four_budgets.3.monthly_amount')) || 0;
        const investmentAnnual = Number(getValues('section_four_budgets.3.annual_amount')) || 0;

        setValue('section_four_budgets.1.monthly_amount', formatAmountDisplayValue(monthlyTotal) as any);
        setValue('section_four_budgets.2.monthly_amount', formatAmountDisplayValue(totalIncomeMonthly - monthlyTotal) as any);

        setSectionTwoFixedAnnualIncome(annualTotal);
        setValue('section_four_budgets.1.annual_amount', formatAmountDisplayValue(annualTotal) as any);
        setValue('section_four_budgets.2.annual_amount', formatAmountDisplayValue(totalIncomeAnnual - annualTotal) as any);

        setValue('section_four_budgets.4.monthly_amount', formatAmountDisplayValue(totalIncomeMonthly - monthlyTotal - investmentMonthly) as any);
        setValue('section_four_budgets.4.annual_amount', formatAmountDisplayValue(totalIncomeAnnual - annualTotal - investmentAnnual) as any);

        calculateSectionFourIncome();
    };
    const calculateSectionTwoVariableIncome = () => {
        let monthlyTotal = 0, annualTotal = 0;
        getValues().section_two_variable_expense.forEach((field: any) => {
            monthlyTotal += +field.monthly_amount || 0;
        });
        getValues().section_two_variable_expense.forEach((field: any) => {
            annualTotal += +field.annual_amount || 0;
        })

        setSectionTwoVariableMonthlyIncome(monthlyTotal);
        setSectionTwoVariableAnnualIncome(annualTotal);

        setValue('section_four_budgets.5.monthly_amount', formatAmountDisplayValue(monthlyTotal) as any);
        setValue('section_four_budgets.5.annual_amount', formatAmountDisplayValue(annualTotal) as any);

        calculateSectionFourIncome()
    };
    const calculateSectionThreeIncome = () => {
        let monthlyTotal = 0, annualTotal = 0;
        getValues().section_three_investments.forEach((field: any) => {
            monthlyTotal += +field.monthly_amount || 0;
        });
        getValues().section_three_investments.forEach((field: any) => {
            annualTotal += +field.annual_amount || 0;
        });

        setSectionThreeMonthlyIncome(monthlyTotal);
        const netIncomeMonthly = Number(getValues('section_four_budgets.2.monthly_amount')) || 0;
        const netIncomeAnnual = Number(getValues('section_four_budgets.2.annual_amount')) || 0;

        setValue('section_four_budgets.3.monthly_amount', formatAmountDisplayValue(monthlyTotal) as any);
        setValue('section_four_budgets.4.monthly_amount', formatAmountDisplayValue(netIncomeMonthly - monthlyTotal) as any);

        setSectionThreeAnnualIncome(annualTotal);
        setValue('section_four_budgets.3.annual_amount', formatAmountDisplayValue(annualTotal) as any);
        setValue('section_four_budgets.4.annual_amount', formatAmountDisplayValue(netIncomeAnnual - annualTotal) as any);

        calculateSectionFourIncome()
    };
    const calculateSectionFourIncome = () => {
        let monthlyTotal = 0, annualTotal = 0;
        getValues().section_four_budgets.forEach((field) => {
            monthlyTotal += +field.monthly_amount || 0;
        });
        getValues().section_four_budgets.forEach((field) => {
            annualTotal += +field.annual_amount || 0;
        });

        setSectionFourMonthlyIncome(monthlyTotal);
        setSectionFourAnnualIncome(annualTotal);
    };

    const { fields: sectionOnePrimaryIncomeFields, append: addMoreSectionOnePrimaryIncome, remove: deleteSectionOnePrimaryIncome } = useFieldArray({
        control,
        name: 'section_one_primary_income',
    });
    const { fields: sectionOneOtherIncomeFields, append: addMoreSectionOneOtherIncome, remove: deleteSectionOneOtherIncome } = useFieldArray({
        control,
        name: 'section_one_other_income',
    });
    const { fields: sectionTwoFixedExpenseFields, append: addMoreSectionTwoFixedExpense, remove: deleteSectionTwoFixedExpense } = useFieldArray({
        control,
        name: 'section_two_fixed_expense',
    });
    const { fields: sectionTwoVariableExpenseFields, append: addMoreSectionTwoVariableExpense, remove: deleteSectionTwoVariableExpense } = useFieldArray({
        control,
        name: 'section_two_variable_expense',
    });
    const { fields: sectionThreeInvestmentsFields, append: addMoreSectionThreeInvestments, remove: deleteSectionThreeInvestments } = useFieldArray({
        control,
        name: 'section_three_investments',
    });
    const { fields: sectionFourBudgetsFields, append: addMoreSectionFourBudgets, remove: deleteSectionFourBudgets } = useFieldArray({
        control,
        name: 'section_four_budgets',
    });

    const sectionOnePrimaryIncomeValues = watch('section_one_primary_income') || [];
    const sectionOneOtherIncomeValues = watch('section_one_other_income') || [];
    const sectionTwoFixedExpenseValues = watch('section_two_fixed_expense') || [];
    const sectionTwoVariableExpenseValues = watch('section_two_variable_expense') || [];
    const sectionThreeInvestmentValues = watch('section_three_investments') || [];
    const sectionFourBudgetValues = watch('section_four_budgets') || [];

    const getMergedSectionRows = (fields: any[], values: any[] = []) =>
        fields.map((field: any, index: number) => ({
            ...field,
            ...values[index],
            originalIndex: index,
        }));

    const getSortedSectionRows = (rows: any[], section: MainSectionKey) => {
        const currentSort = sectionSortConfig[section];

        if (!currentSort.field || !currentSort.direction) {
            return rows;
        }

        return [...rows].sort((left: any, right: any) => {
            const leftValue = normalizeAmountValue(left[currentSort.field as SortField]);
            const rightValue = normalizeAmountValue(right[currentSort.field as SortField]);

            if (leftValue === rightValue) {
                return left.originalIndex - right.originalIndex;
            }

            return currentSort.direction === 'asc'
                ? leftValue - rightValue
                : rightValue - leftValue;
        });
    };

    const sectionOnePrimaryRows = useMemo(
        () => getSortedSectionRows(
            getMergedSectionRows(sectionOnePrimaryIncomeFields, sectionOnePrimaryIncomeValues),
            'section1'
        ),
        [sectionOnePrimaryIncomeFields, sectionOnePrimaryIncomeValues, sectionSortConfig]
    );
    const sectionOneOtherRows = useMemo(
        () => getSortedSectionRows(
            getMergedSectionRows(sectionOneOtherIncomeFields, sectionOneOtherIncomeValues),
            'section1'
        ),
        [sectionOneOtherIncomeFields, sectionOneOtherIncomeValues, sectionSortConfig]
    );
    const sectionTwoFixedRows = useMemo(
        () => getSortedSectionRows(
            getMergedSectionRows(sectionTwoFixedExpenseFields, sectionTwoFixedExpenseValues),
            'section2'
        ),
        [sectionTwoFixedExpenseFields, sectionTwoFixedExpenseValues, sectionSortConfig]
    );
    const sectionTwoVariableRows = useMemo(
        () => getSortedSectionRows(
            getMergedSectionRows(sectionTwoVariableExpenseFields, sectionTwoVariableExpenseValues),
            'section2'
        ),
        [sectionTwoVariableExpenseFields, sectionTwoVariableExpenseValues, sectionSortConfig]
    );
    const sectionThreeRows = useMemo(
        () => getSortedSectionRows(
            getMergedSectionRows(sectionThreeInvestmentsFields, sectionThreeInvestmentValues),
            'section3'
        ),
        [sectionThreeInvestmentsFields, sectionThreeInvestmentValues, sectionSortConfig]
    );
    const sectionFourRows = useMemo(
        () => getSortedSectionRows(
            getMergedSectionRows(sectionFourBudgetsFields, sectionFourBudgetValues),
            'section4'
        ),
        [sectionFourBudgetsFields, sectionFourBudgetValues, sectionSortConfig]
    );

    const toggleSectionSort = (section: MainSectionKey, field: SortField) => {
        setSectionSortConfig((prev) => ({
            ...prev,
            [section]: {
                field,
                direction:
                    (prev[section].field === field) && (prev[section].direction === 'asc')
                        ? 'desc'
                        : 'asc',
            },
        }));
    };

    const getSectionSortDirection = (section: MainSectionKey, field: SortField) =>
        sectionSortConfig[section].field === field
            ? sectionSortConfig[section].direction
            : null;


    const _handleChangeSearchParam = (e: any, type: string) => {
        if (type === 'month') {
            setSearchParams({
                ...searchParams,
                'month': e
            })
        } else if (type === 'year') {
            setSearchParams({
                ...searchParams,
                'year': e
            })
        }
    }

    const fetchMonthlyIncome = () => {
        setIsLoading(true);
        const data = {
            // month: searchParams['month']['value'],
            year: searchParams['year']['value'],
            branch_id: +branchData['id']
        }
        getPersonalIncomeStatment(data, (message: string, resp: any) => {
            const data = resp.data.data;
            reset();
            setSectionSortConfig(getDefaultSortConfig());

            if (data) {
                setIsEdit(true)
                data['section_one_primary_income'].forEach((item: any, index: number) => {
                    addMoreSectionOnePrimaryIncome({ ...normalizeLoadedAmountRow(item), title_read_only: true, monthly_amount_read_only: false, annual_amount_read_only: true })
                    clearLoadedZeroAmount(`section_one_primary_income.${index}.monthly_amount`, item.monthly_amount);
                    clearLoadedZeroAmount(`section_one_primary_income.${index}.annual_amount`, item.annual_amount);
                })
                data['section_one_other_income'].forEach((item: any, index: number) => {
                    addMoreSectionOneOtherIncome({ ...normalizeLoadedAmountRow(item), title_read_only: index !== (data['section_one_other_income'].length - 1), monthly_amount_read_only: false, annual_amount_read_only: true })
                    clearLoadedZeroAmount(`section_one_other_income.${index}.monthly_amount`, item.monthly_amount);
                    clearLoadedZeroAmount(`section_one_other_income.${index}.annual_amount`, item.annual_amount);
                })
                data['section_two_fixed_expense'].forEach((item: any, index: number) => {
                    addMoreSectionTwoFixedExpense({ ...normalizeLoadedAmountRow(item), title_read_only: index !== (data['section_two_fixed_expense'].length - 1), monthly_amount_read_only: false, annual_amount_read_only: true })
                    clearLoadedZeroAmount(`section_two_fixed_expense.${index}.monthly_amount`, item.monthly_amount);
                    clearLoadedZeroAmount(`section_two_fixed_expense.${index}.annual_amount`, item.annual_amount);
                })
                data['section_two_variable_expense'].forEach((item: any, index: number) => {
                    addMoreSectionTwoVariableExpense({ ...normalizeLoadedAmountRow(item), title_read_only: index !== (data['section_two_variable_expense'].length - 1), monthly_amount_read_only: false, annual_amount_read_only: true })
                    clearLoadedZeroAmount(`section_two_variable_expense.${index}.monthly_amount`, item.monthly_amount);
                    clearLoadedZeroAmount(`section_two_variable_expense.${index}.annual_amount`, item.annual_amount);
                })
                data['section_three_investments'].forEach((item: any, index: number) => {
                    addMoreSectionThreeInvestments({
                        ...normalizeLoadedAmountRow(item),
                        title_read_only: index !== (data['section_three_investments'].length - 1),
                        monthly_amount_read_only: index === 0,
                        annual_amount_read_only: index !== 0
                    })
                    clearLoadedZeroAmount(`section_three_investments.${index}.monthly_amount`, item.monthly_amount);
                    clearLoadedZeroAmount(`section_three_investments.${index}.annual_amount`, item.annual_amount);
                })
                data['section_four_budgets'].forEach((item: any) => {
                    addMoreSectionFourBudgets({ ...normalizeLoadedAmountRow(item), title_read_only: true, monthly_amount_read_only: true, annual_amount_read_only: true })
                })

                calculateSectionOneTotalIncome();
                calculateSectionTwoFixedIncome();
                calculateSectionTwoVariableIncome();
                calculateSectionThreeIncome();
                calculateSectionFourIncome();

                setTimeout(() => {
                    window.scrollTo(0, 0)
                }, 0);
            } else {
                setIsEdit(false);
                reset(initData);
                initialiseForm();
            }

            setIsLoading(false);
        }, (message: string, resp: any) => {
            setIsLoading(false);
        })
    }

    const saveMonthlyIncome = (data: any) => {
        setIsLoading(true);
        const normalizeAmount = (value: any) => normalizeAmountValue(value);
        const normalizeAmountRows = (rows: any[] = []) => rows.map((item: any) => ({
            ...item,
            monthly_amount: normalizeAmount(item.monthly_amount),
            annual_amount: normalizeAmount(item.annual_amount),
        }));
        const normalizedData = {
            ...data,
            section_one_primary_income: normalizeAmountRows(data.section_one_primary_income),
            section_one_other_income: normalizeAmountRows(data.section_one_other_income),
            section_two_fixed_expense: normalizeAmountRows(data.section_two_fixed_expense),
            section_two_variable_expense: normalizeAmountRows(data.section_two_variable_expense),
            section_three_investments: normalizeAmountRows(data.section_three_investments),
            section_four_budgets: normalizeAmountRows(data.section_four_budgets),
        };
        const payload = {
            queryString: {
                year: searchParams['year']['value'],
                branch_id: +branchData['id']
            },
            data: normalizedData
        }

        createPersonalIncomeStatment(payload, (message: string, resp: any) => {
            captureContentAndGeneratePDF();
            // generatePdf();
            setIsLoading(false);
            toast.success(message);
            // reset(initData);
            // initialiseForm();
        }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.error(message);
        })
    }

    const submitForm = () => {
        handleSubmit(saveMonthlyIncome)();
    };

    const generatePdf = async () => {
        const element = document.querySelector('.personal-balance-sheet.main-table') as HTMLElement;

        if (!element) {
            alert('Table not found!');
            return;
        }

        // Use html2pdf.js to generate the PDF
        const opt = {
            margin: 0.5,
            filename: 'personal_balance_sheet.pdf',
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
        };

        html2pdf()
            .from(element)
            .set(opt)
            .save();
    };


    const captureContentAndGeneratePDF = async () => {
        const pdf = new jsPDF('p', 'pt', 'a4');

        for (let i = 0; i < pagesRefs.current.length; i++) {
            const pageRef = pagesRefs.current[i];

            if (pageRef === null) {
                continue; // Skip if ref is null
            }

            // Capture the content of each page
            const canvas = await html2canvas(pageRef, { scale: 3 });

            // Add a new page if not the first page
            if (i > 0) {
                pdf.addPage();
            }

            // Add the captured content as an image to the PDF
            pdf.addImage(canvas.toDataURL('image/jpeg', 1.0), 'JPEG', 0, 0, pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight());
        }

        // Save or download the PDF
        pdf.save(`Personal/Family Income Statement(${searchParams['year']['value']}).pdf`);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
        const target = event.target as HTMLInputElement;
        const isAmountInput = (target?.tagName === 'INPUT') && target.classList.contains('amount-input');
        const isReplacementKey = /^[0-9.]$/.test(event.key) || (event.key === '-');

        if (
            isAmountInput
            && !target.readOnly
            && !event.ctrlKey
            && !event.metaKey
            && !event.altKey
            && isReplacementKey
            && isDefaultZeroDisplayValue(target.value)
        ) {
            const nextValue = event.key === '.' ? '0.' : sanitizeAmountInputValue(event.key);
            target.value = nextValue;
            target.dispatchEvent(new Event('input', { bubbles: true }));
            target.dispatchEvent(new Event('change', { bubbles: true }));
            window.requestAnimationFrame(() => {
                const cursorPosition = target.value.length;

                if (typeof target.setSelectionRange === 'function') {
                    target.setSelectionRange(cursorPosition, cursorPosition);
                }
            });
            event.preventDefault();
            return;
        }

        if (
            (target?.tagName === 'INPUT')
            && target.classList.contains('monthly-amount-input')
            && ((event.key === 'ArrowUp') || (event.key === 'ArrowDown'))
        ) {
            event.preventDefault();
            applyMonthlyAmountStep(target, event.key === 'ArrowUp' ? 'up' : 'down');
            return;
        }

        if ((event.key === 'Enter') || (event.keyCode === 13)) {
            event.preventDefault();
        }
    };

    return (
        <div className="main-container flex-grow-1">
            <div className="container-fluid">
                 <div className="d-flex justify-content-between align-items-center flex-wrap pb-3 pt-2">
                        <div>
                            <h1 className="h5 font-weight-700 mb-0 d-inline-flex text-black">{t('pageTitle')}</h1>
                        </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div className="filter-wrap">
                                <div className="filter-box">
                                    <Select
                                        onChange={(e: any) => _handleChangeSearchParam(e, 'year')}
                                        options={lastYears(5)}
                                        defaultValue={searchParams['year']}
                                        className={'zindex-10'}
                                    />
                                </div>

                                <div className="filter-btn">
                                    <button type="button" onClick={fetchMonthlyIncome}><img src={filterSolidIcon} alt="" />{t('generateForm')}</button>
                                </div>
                                <div className="filter-btn">
                                    <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
                                        <div className="loader"></div>
                                    </div>
                                </div>
                            </div>
                </div>
                </div>
             
                <div>
                    <div className="executive-dashboard"
                       style={{  maxHeight: 'calc(100vh - 170px)', overflowY: 'auto' }}
                    ref={tableContainerRef}
                    >
                        <Form onKeyDown={handleKeyDown}>
                            <div>
                                <table className="personal-balance-sheet main-table w-100" ref={ref => pagesRefs.current[0] = ref}>
                                    <tr className="header">
                                        <th className='widthTitle' >{t('section1')}</th>
                                        <th className='text-center widthMonthAnnual' >
                                            <button
                                                type='button'
                                                className='section-main-sort-btn'
                                                onClick={() => toggleSectionSort('section1', 'monthly_amount')}
                                            >
                                                <span>{t('monthlyBudget')}</span>
                                                <SortIndicatorIcon direction={getSectionSortDirection('section1', 'monthly_amount')} />
                                            </button>
                                        </th>
                                        <th className='text-center widthMonthAnnual' >
                                            <button
                                                type='button'
                                                className='section-main-sort-btn'
                                                onClick={() => toggleSectionSort('section1', 'annual_amount')}
                                            >
                                                <span>{t('annualAmount')}</span>
                                                <SortIndicatorIcon direction={getSectionSortDirection('section1', 'annual_amount')} />
                                            </button>
                                        </th>
                                        <th className='text-center widthNotes' >{t('notes')}</th>
                                    </tr>
                                    <tr className='grey-background'>
                                        <td className='header-title'>{t('primaryIncome')}</td>
                                        <td className='header-title-extra-tds'></td>
                                        <td className='header-title-extra-tds'></td>
                                        <td className='header-title-extra-tds'></td>
                                    </tr>
                                    {sectionOnePrimaryRows.map((item: any) => {
                                        const index = item.originalIndex;

                                        return (
                                        <tr key={item.id}>
                                            <td className='titleColumn' >
                                                {item.title}
                                            </td>
                                            <td className='whiteColumn' >
                                                <div className='number-input-with-stepper'>
                                                    <div className='number-stepper' aria-hidden='true'>
                                                        <button type='button' tabIndex={-1} onMouseDown={(e) => handleMonthlyAmountStep(e, 'up')}>▲</button>
                                                        <button type='button' tabIndex={-1} onMouseDown={(e) => handleMonthlyAmountStep(e, 'down')}>▼</button>
                                                    </div>
                                                    <input
                                                        type='text'
                                                        inputMode='decimal'
                                                        onInput={restrictToTwoDecimals}
                                                        {...register(`section_one_primary_income.${index}.monthly_amount`, getNumericInputRegisterOptions({
                                                            onFocus: (e: React.FocusEvent<HTMLInputElement>) => handleMonthlyAmountFocus(e.target.value, e.target),
                                                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleMonthlyAmountChange(e.target.value, `section_one_primary_income.${index}.annual_amount`, calculateSectionOneTotalIncome),
                                                            onBlur: (e: React.FocusEvent<HTMLInputElement>) => handleMonthlyAmountBlur(`section_one_primary_income.${index}.monthly_amount`, `section_one_primary_income.${index}.annual_amount`, e.target.value, calculateSectionOneTotalIncome),
                                                        }))}
                                                        placeholder="0"
                                                        readOnly={item.monthly_amount_read_only}
                                                        className={`amount-input monthly-amount-input ${item.monthly_amount_read_only ? 'input-disabled-look' : ''}`.trim()}
                                                    />
                                                </div>
                                            </td>
                                            <td className='blueColumn' >
                                                <input
                                                    type='text'
                                                    inputMode='decimal'
                                                    onInput={restrictToTwoDecimals}
                                                    {...register(`section_one_primary_income.${index}.annual_amount`, getNumericInputRegisterOptions())}
                                                    placeholder="0"
                                                    readOnly={item.annual_amount_read_only}
                                                    className={`amount-input annual-amount-input ${item.annual_amount_read_only ? 'input-disabled-look' : ''}`.trim()}
                                                />
                                            </td>
                                            <td className='noteColumn' >
                                                <input
                                                    type='text'
                                                    {...register(`section_one_primary_income.${index}.notes`)}
                                                // placeholder="Note"

                                                />
                                            </td>
                                            {/* <button type="button" onClick={() => deleteAnnualIncome(index)}>Remove</button> */}
                                        </tr>
                                    )})}
                                    <tr className='grey-background'>
                                        <td className='header-title'>{t('otherIncomeSources')}</td>
                                        <td className='header-title-extra-tds'></td>
                                        <td className='header-title-extra-tds'></td>
                                        <td className='header-title-extra-tds'></td>
                                    </tr>
                                    {sectionOneOtherRows.map((item: any) => {
                                        const index = item.originalIndex;

                                        return (
                                        <tr key={item.id}>
                                            <td className='titleColumn' >
                                                {item.title}
                                            </td>
                                            <td className='whiteColumn' >
                                                <div className='number-input-with-stepper'>
                                                    <div className='number-stepper' aria-hidden='true'>
                                                        <button type='button' tabIndex={-1} onMouseDown={(e) => handleMonthlyAmountStep(e, 'up')}>▲</button>
                                                        <button type='button' tabIndex={-1} onMouseDown={(e) => handleMonthlyAmountStep(e, 'down')}>▼</button>
                                                    </div>
                                                    <input
                                                        type='text'
                                                        inputMode='decimal'
                                                        onInput={restrictToTwoDecimals}
                                                        {...register(`section_one_other_income.${index}.monthly_amount`, getNumericInputRegisterOptions({
                                                            onFocus: (e: React.FocusEvent<HTMLInputElement>) => handleMonthlyAmountFocus(e.target.value, e.target),
                                                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleMonthlyAmountChange(e.target.value, `section_one_other_income.${index}.annual_amount`, calculateSectionOneTotalIncome),
                                                            onBlur: (e: React.FocusEvent<HTMLInputElement>) => handleMonthlyAmountBlur(`section_one_other_income.${index}.monthly_amount`, `section_one_other_income.${index}.annual_amount`, e.target.value, calculateSectionOneTotalIncome),
                                                        }))}
                                                        placeholder="0"
                                                        readOnly={item.monthly_amount_read_only}
                                                        className='amount-input monthly-amount-input'
                                                    />
                                                </div>
                                            </td>
                                            <td className='blueColumn' >
                                                <input
                                                    type='text'
                                                    inputMode='decimal'
                                                    onInput={restrictToTwoDecimals}
                                                    {...register(`section_one_other_income.${index}.annual_amount`, getNumericInputRegisterOptions())}
                                                    placeholder="0"
                                                    readOnly={item.annual_amount_read_only}
                                                    className={`amount-input annual-amount-input ${item.annual_amount_read_only ? 'input-disabled-look' : ''}`.trim()}
                                                />
                                            </td>
                                            <td className='noteColumn' >
                                                <input
                                                    type='text'
                                                    {...register(`section_one_other_income.${index}.notes`)}
                                                // placeholder="Note"
                                                />
                                            </td>
                                            {/* <button type="button" onClick={() => deleteAnnualIncome(index)}>Remove</button> */}
                                        </tr>
                                    )})}
                                    {
                                        // !isEdit && (
                                        //     <tr>
                                        //         <td >
                                        //             <button type="button" className='buttonAddRow' onClick={() => addMoreSectionOneOtherIncome({ title: '', monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: false, monthly_amount_read_only: false, annual_amount_read_only: false })}>Add Row</button>
                                        //         </td>
                                        //     </tr>
                                        // )
                                    }

                                    <tr className="total-row">
                                        <td className='text-left'>{t('totalHouseholdIncome')}</td>
                                        <td className='amount-cell'>{formatToUSD(parseFloat('' + totalSectionOneMonthlyIncome))}</td>
                                        <td className='amount-cell'>{formatToUSD(parseFloat('' + totalSectionOneAnnualIncome))}</td>
                                        <td className='bg-white'></td>
                                    </tr>
                                    <tr><td style={{ height: '20px' }}></td></tr>
                                    <tr className="header">
                                        <td >{t('section2')}</td>
                                        <td >
                                            <button
                                                type='button'
                                                className='section-main-sort-btn'
                                                onClick={() => toggleSectionSort('section2', 'monthly_amount')}
                                            >
                                                <span>{t('monthlyBudget')}</span>
                                                <SortIndicatorIcon direction={getSectionSortDirection('section2', 'monthly_amount')} />
                                            </button>
                                        </td>
                                        <td >
                                            <button
                                                type='button'
                                                className='section-main-sort-btn'
                                                onClick={() => toggleSectionSort('section2', 'annual_amount')}
                                            >
                                                <span>{t('annualAmount')}</span>
                                                <SortIndicatorIcon direction={getSectionSortDirection('section2', 'annual_amount')} />
                                            </button>
                                        </td>
                                        <td >{t('notes')}</td>
                                    </tr>
                                    <tr className='grey-background'>
                                        <td className='header-title'>{t('expensesFixed')}</td>
                                        <td className='header-title-extra-tds'></td>
                                        <td className='header-title-extra-tds'></td>
                                        <td className='header-title-extra-tds'></td>
                                    </tr>
                                    {sectionTwoFixedRows.map((item: any) => {
                                        const index = item.originalIndex;

                                        return (
                                        <tr key={item.id}>
                                            <td className='titleColumn' >
                                                {item.title}
                                            </td>
                                            <td className='whiteColumn' >
                                                <div className='number-input-with-stepper'>
                                                    <div className='number-stepper' aria-hidden='true'>
                                                        <button type='button' tabIndex={-1} onMouseDown={(e) => handleMonthlyAmountStep(e, 'up')}>▲</button>
                                                        <button type='button' tabIndex={-1} onMouseDown={(e) => handleMonthlyAmountStep(e, 'down')}>▼</button>
                                                    </div>
                                                    <input
                                                        type='text'
                                                        inputMode='decimal'
                                                        onInput={restrictToTwoDecimals}
                                                        {...register(`section_two_fixed_expense.${index}.monthly_amount`, getNumericInputRegisterOptions({
                                                            onFocus: (e: React.FocusEvent<HTMLInputElement>) => handleMonthlyAmountFocus(e.target.value, e.target),
                                                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleMonthlyAmountChange(e.target.value, `section_two_fixed_expense.${index}.annual_amount`, calculateSectionTwoFixedIncome),
                                                            onBlur: (e: React.FocusEvent<HTMLInputElement>) => handleMonthlyAmountBlur(`section_two_fixed_expense.${index}.monthly_amount`, `section_two_fixed_expense.${index}.annual_amount`, e.target.value, calculateSectionTwoFixedIncome),
                                                        }))}
                                                        placeholder="0"
                                                        readOnly={item.monthly_amount_read_only}
                                                        className='amount-input monthly-amount-input'
                                                    />
                                                </div>
                                            </td>
                                            <td className='blueColumn' >
                                                <input
                                                    type='text'
                                                    inputMode='decimal'
                                                    onInput={restrictToTwoDecimals}
                                                    {...register(`section_two_fixed_expense.${index}.annual_amount`, getNumericInputRegisterOptions())}
                                                    placeholder="0"
                                                    readOnly={item.annual_amount_read_only}
                                                    className='amount-input annual-amount-input'
                                                />
                                            </td>
                                            <td className='noteColumn' >
                                                <input
                                                    type='text'
                                                    {...register(`section_two_fixed_expense.${index}.notes`)}
                                                // placeholder="Note"
                                                />
                                            </td>
                                            {/* <button type="button" onClick={() => deleteAnnualIncome(index)}>Remove</button> */}
                                        </tr>
                                    )})}
                                    {
                                        // !isEdit && (
                                        //     <tr>
                                        //         <td >
                                        //             <button type="button" className='buttonAddRow' onClick={() => addMoreSectionTwoFixedExpense({ title: '', monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: false, monthly_amount_read_only: false, annual_amount_read_only: false })}>Add Row</button>
                                        //         </td>
                                        //     </tr>
                                        // )
                                    }

                                    <tr className="total-row-pink">
                                        <td className='text-left'>{t('totalFixedExpenses')}</td>
                                        <td className='amount-cell'>{formatToUSD(parseFloat('' + totalSectionTwoFixedMonthlyIncome))}</td>
                                        <td className='amount-cell'>{formatToUSD(parseFloat('' + totalSectionTwoFixedAnnualIncome))}</td>
                                        <td className='bg-white'></td>
                                    </tr>
                                    <tr><td style={{ height: '20px' }}></td></tr>
                                    <tr className='grey-background'>
                                        <td className='header-title'>{t('expensesVariable')}</td>
                                        <td className='header-title-extra-tds'></td>
                                        <td className='header-title-extra-tds'></td>
                                        <td className='header-title-extra-tds'></td>
                                    </tr>
                                    {sectionTwoVariableRows.map((item: any) => {
                                        const index = item.originalIndex;

                                        return (
                                        <tr key={item.id}>
                                            <td className='titleColumn' >
                                                {item.title}
                                            </td>
                                            <td className='whiteColumn' >
                                                <div className='number-input-with-stepper'>
                                                    <div className='number-stepper' aria-hidden='true'>
                                                        <button type='button' tabIndex={-1} onMouseDown={(e) => handleMonthlyAmountStep(e, 'up')}>▲</button>
                                                        <button type='button' tabIndex={-1} onMouseDown={(e) => handleMonthlyAmountStep(e, 'down')}>▼</button>
                                                    </div>
                                                    <input
                                                        type='text'
                                                        inputMode='decimal'
                                                        onInput={restrictToTwoDecimals}
                                                        {...register(`section_two_variable_expense.${index}.monthly_amount`, getNumericInputRegisterOptions({
                                                            onFocus: (e: React.FocusEvent<HTMLInputElement>) => handleMonthlyAmountFocus(e.target.value, e.target),
                                                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleMonthlyAmountChange(e.target.value, `section_two_variable_expense.${index}.annual_amount`, calculateSectionTwoVariableIncome),
                                                            onBlur: (e: React.FocusEvent<HTMLInputElement>) => handleMonthlyAmountBlur(`section_two_variable_expense.${index}.monthly_amount`, `section_two_variable_expense.${index}.annual_amount`, e.target.value, calculateSectionTwoVariableIncome),
                                                        }))}
                                                        placeholder="0"
                                                        readOnly={item.monthly_amount_read_only}
                                                        className='amount-input monthly-amount-input'
                                                    />
                                                </div>
                                            </td>
                                            <td className='blueColumn' >
                                                <input
                                                    type='text'
                                                    inputMode='decimal'
                                                    onInput={restrictToTwoDecimals}
                                                    {...register(`section_two_variable_expense.${index}.annual_amount`, getNumericInputRegisterOptions())}
                                                    placeholder="0"
                                                    readOnly={item.annual_amount_read_only}
                                                    className='amount-input annual-amount-input'
                                                />
                                            </td>
                                            <td className='noteColumn' >
                                                <input
                                                    type='text'
                                                    {...register(`section_two_variable_expense.${index}.notes`)}
                                                // placeholder="Note"
                                                />
                                            </td>
                                            {/* <button type="button" onClick={() => deleteAnnualIncome(index)}>Remove</button> */}
                                        </tr>
                                    )})}
                                    {
                                        // !isEdit && (
                                        //     <tr>
                                        //         <td >
                                        //             <button type="button" className='buttonAddRow' onClick={() => addMoreSectionTwoVariableExpense({ title: '', monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: false, monthly_amount_read_only: false, annual_amount_read_only: false })}>Add Row</button>
                                        //         </td>
                                        //     </tr>
                                        // )
                                    }

                                    <tr className="total-row">
                                        <td className="text-left total-row-pink" >{t('totalVariableExpenses')}</td>
                                        <td className="total-row-pink amount-cell">{formatToUSD(parseFloat('' + totalSectionTwoVariableMonthlyIncome))}</td>
                                        <td className="total-row-pink amount-cell">{formatToUSD(parseFloat('' + totalSectionTwoVariableAnnualIncome))}</td>
                                        <td className="bg-white"></td>
                                    </tr>
                                    <tr><td style={{ height: '20px' }}></td></tr>
                                    <tr className="header">
                                        <td >{t('section3')}</td>
                                        <td >
                                            <button
                                                type='button'
                                                className='section-main-sort-btn'
                                                onClick={() => toggleSectionSort('section3', 'monthly_amount')}
                                            >
                                                <span>{t('monthlyBudget')}</span>
                                                <SortIndicatorIcon direction={getSectionSortDirection('section3', 'monthly_amount')} />
                                            </button>
                                        </td>
                                        <td >
                                            <button
                                                type='button'
                                                className='section-main-sort-btn'
                                                onClick={() => toggleSectionSort('section3', 'annual_amount')}
                                            >
                                                <span>{t('annualAmount')}</span>
                                                <SortIndicatorIcon direction={getSectionSortDirection('section3', 'annual_amount')} />
                                            </button>
                                        </td>
                                        <td >{t('notes')}</td>
                                    </tr>
                                    <tr className='grey-background'>
                                        <td className='header-title font-weight-bold' colSpan={6}>{t('investmentSpending')}</td>
                                    </tr>
                                    {sectionThreeRows.map((item: any) => {
                                        const index = item.originalIndex;

                                        return (
                                        <tr key={item.id}>
                                            <td className='titleColumn' >
                                                <input
                                                    type='text'
                                                    {...register(`section_three_investments.${index}.title`)}
                                                    placeholder="Title"
                                                    readOnly={item.title_read_only}
                                                    className={item.title_read_only ? 'input-disabled-look' : ''}
                                                />
                                            </td>
                                            <td className='whiteColumn' >
                                                <div className='number-input-with-stepper'>
                                                    <div className='number-stepper' aria-hidden='true'>
                                                        <button type='button' tabIndex={-1} onMouseDown={(e) => handleMonthlyAmountStep(e, 'up')}>▲</button>
                                                        <button type='button' tabIndex={-1} onMouseDown={(e) => handleMonthlyAmountStep(e, 'down')}>▼</button>
                                                    </div>
                                                    <input
                                                        type='text'
                                                        inputMode='decimal'
                                                        onInput={restrictToTwoDecimals}
                                                        {...register(`section_three_investments.${index}.monthly_amount`, getNumericInputRegisterOptions({
                                                            onFocus: (e: React.FocusEvent<HTMLInputElement>) => handleMonthlyAmountFocus(e.target.value, e.target),
                                                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleMonthlyAmountChange(e.target.value, `section_three_investments.${index}.annual_amount`, calculateSectionThreeIncome),
                                                            onBlur: (e: React.FocusEvent<HTMLInputElement>) => handleMonthlyAmountBlur(`section_three_investments.${index}.monthly_amount`, `section_three_investments.${index}.annual_amount`, e.target.value, calculateSectionThreeIncome),
                                                        }))}
                                                        placeholder="0"
                                                        readOnly={item.monthly_amount_read_only}
                                                        className={`amount-input monthly-amount-input ${item.monthly_amount_read_only ? 'input-disabled-look' : ''}`.trim()}
                                                    />
                                                </div>
                                            </td>
                                            <td className='blueColumn' >
                                                <input
                                                    type='text'
                                                    inputMode='decimal'
                                                    onInput={restrictToTwoDecimals}
                                                    {...register(`section_three_investments.${index}.annual_amount`, getNumericInputRegisterOptions())}
                                                    placeholder="0"
                                                    readOnly={item.annual_amount_read_only}
                                                    onFocus={(e) => handleMonthlyAmountFocus(e.target.value, e.target)}
                                                    onChange={(e) => {
                                                        const annualAmount = normalizeAmountValue(e.target.value);
                                                        const monthlyAmount = normalizeAmountValue(annualAmount / 12);

                                                        setValue(`section_three_investments.${index}.annual_amount`, annualAmount as any);
                                                        setValue(`section_three_investments.${index}.monthly_amount`, monthlyAmount as any);
                                                        calculateSectionThreeIncome();
                                                    }}
                                                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                                                        const annualAmount = normalizeAmountValue(e.target.value);
                                                        const monthlyAmount = normalizeAmountValue(annualAmount / 12);

                                                        setValue(`section_three_investments.${index}.annual_amount`, formatAmountDisplayValue(annualAmount) as any);
                                                        setValue(`section_three_investments.${index}.monthly_amount`, formatAmountDisplayValue(monthlyAmount) as any);
                                                        calculateSectionThreeIncome();
                                                    }}
                                                    className={`amount-input annual-amount-input ${item.annual_amount_read_only ? 'input-disabled-look' : ''}`.trim()}
                                                />
                                            </td>
                                            <td className='noteColumn' >
                                                <input
                                                    type='text'
                                                    {...register(`section_three_investments.${index}.notes`)}
                                                // placeholder="Note"
                                                />
                                            </td>
                                            {/* <button type="button" onClick={() => deleteAnnualIncome(index)}>Remove</button> */}
                                        </tr>
                                    )})}
                                    {
                                        // !isEdit && (
                                        //     <tr>
                                        //         <td >
                                        //             <button type="button" className='buttonAddRow' onClick={() => addMoreSectionThreeInvestments({ title: '', monthly_amount: 0, annual_amount: 0, notes: '', title_read_only: false, monthly_amount_read_only: false, annual_amount_read_only: true })}>Add Row</button>
                                        //         </td>
                                        //     </tr>
                                        // )
                                    }

                                    <tr className="total-row">
                                        <td className="text-left total-row-pink" >{t('totalInvestmentCosts')}</td>
                                        <td className="total-row-pink amount-cell">{formatToUSD(parseFloat('' + totalSectionThreeMonthlyIncome))}</td>
                                        <td className="total-row-pink amount-cell">{formatToUSD(parseFloat('' + totalSectionThreeAnnualIncome))}</td>
                                        <td className="bg-white"></td>
                                    </tr>
                                    <tr><td style={{ height: '20px' }}></td></tr>
                                    <tr className="header">
                                        <td >{t('section4')}</td>
                                        <td >
                                            <button
                                                type='button'
                                                className='section-main-sort-btn'
                                                onClick={() => toggleSectionSort('section4', 'monthly_amount')}
                                            >
                                                <span>{t('monthlyBudget')}</span>
                                                <SortIndicatorIcon direction={getSectionSortDirection('section4', 'monthly_amount')} />
                                            </button>
                                        </td>
                                        <td >
                                            <button
                                                type='button'
                                                className='section-main-sort-btn'
                                                onClick={() => toggleSectionSort('section4', 'annual_amount')}
                                            >
                                                <span>{t('annualAmount')}</span>
                                                <SortIndicatorIcon direction={getSectionSortDirection('section4', 'annual_amount')} />
                                            </button>
                                        </td>
                                        <td >{t('notes')}</td>
                                    </tr>
                                    {sectionFourRows.map((item: any) => {
                                        const index = item.originalIndex;

                                        return (
                                        <tr className={`titleColumn ${item.title === t('variableExpenses') ? 'total-row-yellow' : ''} `} key={item.id}>
                                            <td >
                                                <input
                                                    type='text'
                                                    {...register(`section_four_budgets.${index}.title`)}
                                                    placeholder={t('variableExpenses')}
                                                    readOnly={item.title_read_only}
                                                    className={item.title_read_only ? 'input-disabled-look' : ''}
                                                />
                                            </td>
                                            <td className='titleColumn' >
                                                <input
                                                    type='text'
                                                    inputMode='decimal'
                                                    onInput={restrictToTwoDecimals}
                                                    {...register(`section_four_budgets.${index}.monthly_amount`, getNumericInputRegisterOptions())}
                                                    placeholder="0"
                                                    readOnly={item.monthly_amount_read_only}
                                                    className={`amount-input annual-amount-input ${item.monthly_amount_read_only ? 'input-disabled-look' : ''}`.trim()}
                                                />
                                            </td>
                                            <td className='titleColumn' >
                                                <input
                                                    type='text'
                                                    inputMode='decimal'
                                                    onInput={restrictToTwoDecimals}
                                                    {...register(`section_four_budgets.${index}.annual_amount`, getNumericInputRegisterOptions())}
                                                    placeholder="0"
                                                    readOnly={item.annual_amount_read_only}
                                                    className={`amount-input annual-amount-input ${item.annual_amount_read_only ? 'input-disabled-look' : ''}`.trim()}
                                                />
                                            </td>
                                            <td className='titleColumn' >
                                                <input
                                                    type='text'
                                                    {...register(`section_four_budgets.${index}.notes`)}
                                                // placeholder={t('notes')}
                                                />
                                            </td>
                                        </tr>
                                    )})}
                                    <tr className="total-row">
                                        <td >{t('disposableIncomeSavings')}</td>
                                        <td className='amount-cell'>{formatToUSD(parseFloat('' + totalSectionFourMonthlyIncome))}</td>
                                        <td className='amount-cell'>{formatToUSD(parseFloat('' + totalSectionFourAnnualIncome))}</td>
                                        <td className='bg-white'></td>
                                    </tr>
                                </table>
                            </div>
                            <button type="button" onClick={submitForm} className="btn btn-primary mt-4">
                                {(showLoader && (<Spinner animation="border" size="sm" />)) || t('saveAndGeneratePDF')}
                            </button>


                        </Form>
                    </div>

                </div>
            <ScrollToggle scrollRef={tableContainerRef} />
            </div>
        </div>

    )
}

export default PersonalIncomeStatement;
